import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";

interface SeedBrandCandidate {
  id: number;
  seedJobId: number;
  niche: string;
  brandName: string;
  metaPageId: string | null;
  metaPageName: string | null;
  adCount: number | null;
  verified: boolean;
  approved: boolean;
  scanStatus: string;
}

export function SeederBrandApprovalGrid({
  jobId,
  candidates,
}: {
  jobId: number;
  candidates: SeedBrandCandidate[];
}) {
  const queryClient = useQueryClient();

  // Default-select all verified candidates
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(candidates.filter((c) => c.verified).map((c) => c.id))
  );

  const groupedByNiche = useMemo(() => {
    const map = new Map<string, SeedBrandCandidate[]>();
    for (const c of candidates) {
      if (!map.has(c.niche)) map.set(c.niche, []);
      map.get(c.niche)!.push(c);
    }
    return map;
  }, [candidates]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const approveAndScan = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/admin/seed-jobs/${jobId}/approve-brands`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ candidateIds: Array.from(selected) }),
      });
      if (!res.ok) throw new Error((await res.json()).message || "Failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/admin/seed-jobs/${jobId}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/seed-jobs"] });
    },
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-[#526074]">
          {selected.size} of {candidates.length} brands selected
        </p>
        <Button
          data-testid="button-approve-and-scan"
          disabled={selected.size === 0 || approveAndScan.isPending}
          onClick={() => approveAndScan.mutate()}
        >
          {approveAndScan.isPending ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Kicking off scan...</>
          ) : (
            `Approve & Scan ${selected.size} Brand${selected.size === 1 ? "" : "s"}`
          )}
        </Button>
      </div>

      {Array.from(groupedByNiche.entries()).map(([niche, brands]) => (
        <section key={niche} className="mb-6">
          <h3 className="text-sm font-semibold mb-2 uppercase tracking-wide text-[#526074]">
            {niche} ({brands.length})
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {brands.map((c) => {
              const isChecked = selected.has(c.id);
              return (
                <label
                  key={c.id}
                  data-testid={`card-candidate-${c.id}`}
                  className={`border rounded p-3 cursor-pointer transition-colors ${isChecked ? "border-blue-500 bg-blue-50" : "border-[#DCE3ED] hover:bg-gray-50"}`}
                >
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggle(c.id)}
                      className="mt-1"
                      data-testid={`checkbox-candidate-${c.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{c.brandName}</div>
                      <div className="text-xs mt-1">
                        {c.verified ? (
                          <span className="text-green-700">
                            {c.adCount != null && c.adCount > 0
                              ? `✅ ${c.adCount} active ads`
                              : "✅ Verified on Meta"}
                          </span>
                        ) : (
                          <span className="text-[#526074]">❌ Not found on Meta</span>
                        )}
                      </div>
                      {c.metaPageName && c.metaPageName !== c.brandName && (
                        <div className="text-xs text-[#526074] mt-0.5 truncate">
                          Page: {c.metaPageName}
                        </div>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
